import { apiService } from './api.service.js'
import { cacheService } from './cache.service.js'
import { AUTH_CONSTANTS } from '../constants/auth.constants.js'

/**
 * Serviço de tags com cache inteligente
 * Otimiza leituras do Firestore mantendo dados no localStorage
 */
export class TagCacheService {
  constructor() {
    this.cacheKey = 'tags'
    this.endpoint = 'tags'
    this.loadingPromise = null
  }

  /**
   * Lista tags com cache inteligente (TTL 10 minutos)
   * @returns {Promise<Array>} Lista de tags
   */
  async listar() {
    if (this._isCacheValid()) {
      return cacheService.get(this.cacheKey)
    }

    if (this.loadingPromise) {
      return this.loadingPromise
    }


    
    this.loadingPromise = this._loadFromBackend()
    const result = await this.loadingPromise
    this.loadingPromise = null
    
    return result
  }

  /**
   * Carrega dados do backend.
   * Em caso de timeout de autenticação (AUTH_TIMEOUT), retorna dados do cache local
   * ou array vazio — sem propagar exceção e sem executar requisição à API.
   *
   * @returns {Promise<Array>} Lista de tags
   * @private
   */
  async _loadFromBackend () {
    try {
      const response = await apiService.get(this.endpoint)

      // Backend retorna { success: true, data: [...] }
      let tags = []
      if (response.data?.data && Array.isArray(response.data.data)) {
        tags = response.data.data
      } else if (Array.isArray(response.data)) {
        tags = response.data
      } else if (Array.isArray(response)) {
        tags = response
      }

      cacheService.set(this.cacheKey, tags)

      return tags
    } catch (error) {
      // Em timeout de auth, retorna cache local ou array vazio (Req 5.2)
      if (error.code === AUTH_CONSTANTS.AUTH_TIMEOUT_ERROR) {
        const cacheLocal = cacheService.get(this.cacheKey)
        return cacheLocal || []
      }
      if (error.message?.includes('autorizado')) {
        return []
      }
      throw error
    }
  }

  /**
   * Verifica se cache é válido (não expirou)
   * @returns {boolean} True se cache válido
   * @private
   */
  _isCacheValid() {
    if (!cacheService.currentUserUid) return false
    
    try {
      const userKey = cacheService._getUserKey(this.cacheKey)
      const cached = localStorage.getItem(userKey)
      if (!cached) return false
      
      const cacheData = JSON.parse(cached)
      
      // Verifica se pertence ao usuário atual
      if (cacheData.uid !== cacheService.currentUserUid) return false
      
      const now = Date.now()
      const cacheAge = now - cacheData.timestamp
      const TTL = 60 * 60 * 1000 // 1 hora
      
      return cacheAge < TTL
    } catch (error) {
      return false
    }
  }

  /**
   * Cria nova tag (backend + cache)
   * @param {Object} dadosTag - Dados da tag
   * @returns {Promise<Object>} Tag criada
   */
  async criar(dadosTag) {
    const response = await apiService.post(this.endpoint, dadosTag)
    const novaTag = response.data?.data || response.data || response
    
    cacheService.addToArray(this.cacheKey, novaTag)
    
    return novaTag
  }

  /**
   * Atualiza tag (backend + cache)
   * @param {string} id - ID da tag
   * @param {Object} dadosAtualizacao - Dados para atualizar
   * @returns {Promise<Object>} Tag atualizada
   */
  async atualizar(id, dadosAtualizacao) {
    const response = await apiService.put(`${this.endpoint}/${id}`, dadosAtualizacao)
    const tagAtualizada = response.data?.data || response.data || response
    
    cacheService.updateInArray(this.cacheKey, id, tagAtualizada)
    
    return tagAtualizada
  }

  /**
   * Exclui tag (backend + cache)
   * @param {string} id - ID da tag
   * @returns {Promise<boolean>} True se excluído
   */
  async excluir(id) {
    await apiService.delete(`${this.endpoint}/${id}`)
    
    cacheService.removeFromArray(this.cacheKey, id)
    
    return true
  }

  /**
   * Busca tag por ID (do cache)
   * @param {string} id - ID da tag
   * @returns {Object|null} Tag encontrada
   */
  buscarPorId(id) {
    const tags = cacheService.get(this.cacheKey) || []
    return tags.find(tag => tag.id === id) || null
  }

  /**
   * Força recarregamento do cache
   * @returns {Promise<Array>} Lista atualizada
   */
  async recarregar() {
    cacheService.remove(this.cacheKey)
    return this.listar()
  }

  /**
   * Limpa cache de tags
   */
  limparCache() {
    cacheService.remove(this.cacheKey)
  }
}

// Instância singleton
export const tagCacheService = new TagCacheService()
